import { useCallback, useState } from 'react';

import { updateProfile } from 'actions/user';
import { IS_INDIAN_EXCHANGE } from 'constants/constants';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import useToggle from 'hooks/useToggle';
import i18n from 'i18n/config';
import { useAppDispatch } from 'storeHooks';
import MaterialConfirmDialog from 'UIKit/MaterialConfirmDialog';

import {
  Complete30DayCard,
  DoAddBankCard,
  DoDepositCard,
  DoKycCard,
  DoTradeCard,
} from './UserJourneyCard.Component';
import styles from './userJourneyCard.module.scss';
import { USER_JOURNEY_CARDS } from './UserJourneyCard.types';

interface CardToViewProps {
  card: USER_JOURNEY_CARDS;
  loading: boolean;
  updateProfileBeforeClose: () => Promise<void>;
}

const CardToView = ({ card, loading, updateProfileBeforeClose }: CardToViewProps) => {
  switch (card) {
    case USER_JOURNEY_CARDS.KYC:
      return <DoKycCard loading={loading} onCloseRequest={updateProfileBeforeClose} />;

    case USER_JOURNEY_CARDS.ADD_BANK:
      return (
        <DoAddBankCard loading={loading} onCloseRequest={updateProfileBeforeClose} />
      );

    case USER_JOURNEY_CARDS.DEPOSIT:
      return (
        <DoDepositCard loading={loading} onCloseRequest={updateProfileBeforeClose} />
      );

    case USER_JOURNEY_CARDS.TRADE:
      return <DoTradeCard loading={loading} onCloseRequest={updateProfileBeforeClose} />;

    case USER_JOURNEY_CARDS.THIRTY_DAYS:
      return (
        <Complete30DayCard onCloseRequest={updateProfileBeforeClose} loading={loading} />
      );

    default:
      return null;
  }
};

const getCardHeadings = () => ({
  [USER_JOURNEY_CARDS.KYC]: i18n.t(
    'markets:userJourneyCards.emailVerificationSuccessFul'
  ),
  [USER_JOURNEY_CARDS.ADD_BANK]: i18n.t(
    'markets:userJourneyCards.identityVerificationSuccessFul'
  ),
  [USER_JOURNEY_CARDS.DEPOSIT]: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.bankAdditionSuccessful')
    : i18n.t('markets:userJourneyCards.identityVerificationSuccessFul'),
  [USER_JOURNEY_CARDS.TRADE]: i18n.t('markets:userJourneyCards.depositSuccessFul'),
  [USER_JOURNEY_CARDS.THIRTY_DAYS]: IS_INDIAN_EXCHANGE
    ? i18n.t('markets:userJourneyCards.firstDepositSuccessFul')
    : i18n.t('markets:userJourneyCards.firstTradeSuccessFul'),
});

const UserJourneyCardsModal = ({ card }: { card: USER_JOURNEY_CARDS }) => {
  const [isOpen, toggleIsOpen] = useToggle(true);

  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const { whenBelowSm } = useCustomMediaQuery();

  const cardHeading = getCardHeadings()[card];

  const updateProfileBeforeClose = useCallback(async () => {
    try {
      setLoading(true);
      await dispatch(updateProfile({ banners: card }, false));

      toggleIsOpen();

      setLoading(false);
    } catch (err) {
      // added alert in epic
    }
  }, [dispatch, card, toggleIsOpen]);

  return (
    <MaterialConfirmDialog
      title={cardHeading}
      headerClassName={IS_INDIAN_EXCHANGE ? styles.leftAlignTitle : ''}
      showHeader={false}
      showFooter={false}
      onClose={updateProfileBeforeClose}
      bodyClassName={styles.modalBody}
      open={isOpen}
      contentClassName={styles.userJourneyContent}
      fullWidth={whenBelowSm}
      testId={`user-journey-card-modal-${card}`}
    >
      <CardToView
        card={card}
        loading={loading}
        updateProfileBeforeClose={updateProfileBeforeClose}
      />
    </MaterialConfirmDialog>
  );
};

export default UserJourneyCardsModal;
